
import { Button } from "@/components/ui/button";
import { Save, Plus, Loader2, AlertCircle, RefreshCw } from "lucide-react";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { SaveDrawingDialog } from "./SaveDrawingDialog";
import { useState, useEffect } from "react";

interface SaveButtonProps {
  onQuickSave: () => Promise<void>;
  onSaveAs: () => Promise<void>;
  onRetry?: () => Promise<void>;
  isSaving: boolean;
  hasTimedOut: boolean;
  hasError?: boolean;
  title: string;
  onTitleChange: (title: string) => void;
  saveDescription: string;
  onDescriptionChange: (description: string) => void;
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  saveAsDialogOpen: boolean;
  setSaveAsDialogOpen: (open: boolean) => void;
  currentDrawingId: string | null;
}

export const SaveButton = ({
  onQuickSave,
  onSaveAs,
  onRetry,
  isSaving,
  hasTimedOut,
  hasError = false,
  title,
  onTitleChange,
  saveDescription,
  onDescriptionChange,
  selectedTags,
  onTagsChange,
  saveAsDialogOpen,
  setSaveAsDialogOpen,
  currentDrawingId,
}: SaveButtonProps) => {
  console.log('SaveButton: Rendering with state:', { isSaving, hasTimedOut, hasError, title, currentDrawingId });
  
  // Local state for dialog title
  const [dialogTitle, setDialogTitle] = useState("Save Drawing");
  
  // Update dialog title based on operation
  useEffect(() => {
    if (!currentDrawingId) {
      setDialogTitle("Save Drawing");
    } else {
      setDialogTitle("Save Drawing As");
    }
  }, [currentDrawingId]);

  const handleQuickSave = async (e: React.MouseEvent) => {
    console.log('SaveButton: Quick save clicked');
    e.preventDefault();
    await onQuickSave();
  };

  const handleSaveAsDialog = async () => {
    console.log('SaveButton: Save as dialog save clicked');
    await onSaveAs();
  };

  const handleRetry = async (e: React.MouseEvent) => {
    console.log('SaveButton: Retry clicked');
    e.preventDefault();
    if (onRetry) {
      await onRetry();
    } else {
      await onQuickSave();
    }
  };

  // Error state button
  if (hasError || hasTimedOut) {
    return (
      <Button
        variant="ghost"
        className="hover:bg-gray-100/80 text-red-500 rounded-full h-9 w-9 p-0 transition-all duration-200"
        size="icon"
        title="Retry save"
        onClick={handleRetry}
      >
        <RefreshCw className="h-4 w-4" />
      </Button>
    );
  }

  // If it's a new drawing (no currentDrawingId), show single Save button
  if (!currentDrawingId) {
    return (
      <Dialog open={saveAsDialogOpen} onOpenChange={setSaveAsDialogOpen}>
        <DialogTrigger asChild>
          <Button
            variant="ghost"
            className="hover:bg-gray-100/80 text-gray-700 rounded-full h-9 w-9 p-0 transition-all duration-200"
            size="icon"
            title="Save drawing"
            disabled={isSaving}
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
          </Button>
        </DialogTrigger>
        <SaveDrawingDialog
          title={title}
          onTitleChange={onTitleChange}
          description={saveDescription}
          onDescriptionChange={onDescriptionChange}
          selectedTags={selectedTags}
          onTagsChange={onTagsChange}
          onSave={handleSaveAsDialog}
          isSaving={isSaving}
          hasTimedOut={hasTimedOut}
          dialogTitle={dialogTitle}
        />
      </Dialog>
    );
  }

  // For existing drawings, show the dropdown with both options
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="hover:bg-gray-100/80 text-gray-700 rounded-full h-9 w-9 p-0 transition-all duration-200"
          size="icon"
          title="Save options"
          disabled={isSaving}
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem 
          onClick={handleQuickSave}
          disabled={isSaving}
          className={`${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          <span>Update Drawing</span>
        </DropdownMenuItem>
        <Dialog open={saveAsDialogOpen} onOpenChange={setSaveAsDialogOpen}>
          <DialogTrigger asChild>
            <DropdownMenuItem 
              onSelect={(e) => e.preventDefault()}
              disabled={isSaving}
              className={isSaving ? 'opacity-50 cursor-not-allowed' : ''}
            >
              <Plus className="h-4 w-4 mr-2" />
              <span>Save As Copy...</span>
            </DropdownMenuItem>
          </DialogTrigger>
          <SaveDrawingDialog
            title={title}
            onTitleChange={onTitleChange}
            description={saveDescription}
            onDescriptionChange={onDescriptionChange}
            selectedTags={selectedTags}
            onTagsChange={onTagsChange}
            onSave={handleSaveAsDialog}
            isSaving={isSaving}
            hasTimedOut={hasTimedOut}
            dialogTitle="Save Drawing As Copy"
          />
        </Dialog>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
